<template>
    <div
        v-if="!isLoading"
        class="study-activity-card"
        v-dark
    >
        <div class="study-activity-card__activity-left" aria-hidden="true">
            <div class="study-activity-card__activity-icon">
                <Icon :type="activityIcon" :style="{ color: activity.iconColor }" />
            </div>
            <div v-dark class="study-activity-card__activity-name">
                {{ isRecallChallengeQuiz ? 'Level Up' : activity.name }}
            </div>
            <div
                v-if="activity.nameSuffix"
                v-dark
                class="study-activity-card__activity-name-suffix"
            >
                <OverflowTooltip>
                    {{ activity.nameSuffix }}
                </OverflowTooltip>
            </div>
            <div
                v-if="levelUpQuizLevel && numOfLevelsInSubject"
                v-dark
                class="study-activity-card__level-up-info"
                :aria-label="`Level ${levelUpQuizLevel} of ${numOfLevelsInSubject}`"
            >
                Level {{ levelUpQuizLevel }}/{{ numOfLevelsInSubject }}
            </div>
            <div
                v-if="isRecallChallengeQuiz"
                v-dark
                class="study-activity-card__recall-challenge-subtitle"
                :aria-label="'recallChallenge'"
            >
                Recall Challenge
            </div>
        </div>
        <div
            v-dark
            class="study-activity-card__activity-date"
            aria-hidden="true"
        >
            {{ activity.date }}
        </div>
        <div class="study-activity-card__activity-score-container" aria-hidden="true">
            <div
                v-dark
                class="study-activity-card__activity-score"
                :class="{
                    [`study-activity-card__activity-score--${activity.scoreRange}`]: true,
                    [`study-activity-card__activity-score--${activity.score}`]:
                        activity.name === 'Question of the Day'
                }"
            >
                <template v-if="activity.name === 'Question of the Day'">
                    <template v-if="activity.isLocked">
                        <Icon
                            v-dark
                            type="lock"
                            class="study-activity-card__lock"
                            title="Locked"
                        />
                    </template>
                    <Icon
                        v-if="activity.score === 'correct'"
                        v-dark
                        type="correct"
                        class="study-activity-card__correct"
                        title="Correct"
                    />
                    <Icon
                        v-else
                        v-dark
                        type="incorrect"
                        class="study-activity-card__incorrect"
                        title="Incorrect"
                    />
                </template>
                <template v-else>
                    {{ activity.score }}
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import UIKit, { type TIconType } from '@pocketprep/ui-kit'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'

const {
    Icon,
    OverflowTooltip,
} = UIKit

type TStudyActivity = {
    quizId: string
    isLocked: boolean
    icon: string
    name: string
    nameSuffix: string
    iconColor: string
    date: string
    score: string
    scoreRange: string
}

const props = withDefaults(
    defineProps<{
        activity: TStudyActivity
        isLoading?: boolean
    }>(),
    {
        isLoading: true,
    }
)

const activityIcon = computed(() => {
    return props.activity.icon as TIconType
})

const currentQuiz = computed(() => {
    return quizModule.getters.getQuizById(props.activity.quizId)
})

const levelUpQuizLevel = computed(() => {
    return currentQuiz.value?.levelUpProgress?.level
})

const levelUpQuizSubjectId = computed(() => {
    return currentQuiz.value?.levelUpProgress?.subjectId
})

const numOfLevelsInSubject = computed(() => {
    return examMetadataModule.getters.getLevelsInSubject(levelUpQuizSubjectId.value)?.length
})

const isRecallChallengeQuiz = computed(() => {
    return currentQuiz.value?.mode === 7
})
</script>

<style lang="scss" scoped>
.study-activity-card {
    flex: 1;
    display: flex;
    width: 100%;
    min-height: 42px;
    max-height: 42px;
    position: relative;
    align-items: center;
    cursor: pointer;
    outline: none;
    box-sizing: border-box;
    padding: 0 0 0 3px;

    &::before {
        content: '';
        position: absolute;
        background: $gray-divider;
        left: 0;
        bottom: 0;
        height: 1px;
        width: 100%;
        z-index: 1;
    }

    &:focus {
        &::after {
            content: '';
            position: absolute;
            left: -10px;
            top: 1px;
            width: calc(100% + 20px);
            height: calc(100% - 3px);
            border: 1px solid $brand-blue;
            border-radius: 6px;
        }
    }

    &:hover {
        background-color: $gray-background;
    }

    &--dark {
        &::before {
            background-color: $ash;
        }

        &:focus::after {
            border-color: $banana-bread;
        }

        &:hover {
            background-color: $brand-black;
        }
    }

    &__activity-left {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
    }

    &__activity-icon {
        min-width: 25px !important;
        height: 24px;
        margin-right: 6px;

        svg {
            width: 100%;
        }
    }

    &__activity-name,
    &__activity-name-suffix {
        font-size: 14px;
        line-height: 19px;
        color: $brand-black;

        &--dark {
            color: white
        }
    }

    &__activity-name-suffix {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100%;
        margin-left: 8px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__level-up-info,
    &__recall-challenge-subtitle {
        color: $ash;
        font-size: 14px;
        line-height: 19px;
        margin-left: 6px;

        &--dark {
            color: $fog;
        }
    }

    &__recall-challenge-subtitle {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        @include breakpoint(black-bear) {
            width: calc(100% - 100px);
        }
    }

    &__activity-date {
        color: $slate;
        font-size: 14px;
        line-height: 19px;
        text-align: right;
        width: 55px;
        flex-shrink: 0;

        &--dark {
            color: rgb(255, 255, 255, 0.70);
        }

    }

    &__activity-score-container {
        width: 70px;
        flex-shrink: 0;
        display: flex;
        justify-content: flex-end;
        margin-left: 5px;
    }

    &__activity-score {
        border-radius: 3px;
        height: 24px;
        padding: 0 5px 0 6px;
        font-weight: 600;
        display: flex;
        align-items: center;
        font-size: 14px;

        &--dark {
            color: $white;
        }

        &--low {
            background: $red-hint;

            &--dark {
                background: rgba($brand-red, 0.3);
            }
        }

        &--medium {
            background: $cream-puff;

            &--dark {
                background: rgba($butterscotch, 0.18);
            }
        }

        &--high {
            background: $barely-green;

            &--dark {
                background: rgba($jungle-green, 0.2);
            }
        }

        &--correct,
        &--incorrect {
            background: transparent !important;
            display: flex;
            justify-content: flex-end;
        }

        &--correct {
            color: $spectral-green;
            width: 48px;

            &--dark {
                color: $jungle-green;
            }
        }

        &--incorrect {
            width: 48px;
            color: $red-pegasus;

            &--dark {
                color: $rosa;
            }
        }
    }

    &__lock {
        width: 22px;
        height: 22px;
        color: $slate;

        &--dark {
            color: $pewter;
        }
    }

    &__correct {
        width: 22px;
        height: 22px;
        color: $cadaverous;

        &--dark {
            color: $jungle-green;
        }
    }

    &__incorrect {
        height: 22px;
        width: 22px;
        color: $pepper;

        &--dark {
            color: $rosa;
        }
    }
}
</style>
